import Divider from '../../../atoms/flightBriefPdf/Divider/Divider';
import typo from '../../../atoms/flightBriefPdf/flightBriefTypography.module.css';
import styles from './HandlingColumn.module.css';

/**
 * One side of the "Airport Handling" section: handling title with an icon
 * badge, terminal name, terminal sub-title, address, phone and an optional
 * meeting-information block.
 * @param {object} props
 * @param {React.ReactNode} [props.titleIcon] - Icon slot inside the badge.
 * @param {React.ReactNode} props.title - Handling title (e.g. "LEI - DEPARTURE HANDLING").
 * @param {React.ReactNode} props.terminalName - Terminal name.
 * @param {React.ReactNode} [props.terminal] - Terminal sub-title (e.g. "Main terminal").
 * @param {React.ReactNode} [props.addressIcon] - Icon slot next to the address.
 * @param {React.ReactNode} [props.address] - Multi-line address.
 * @param {React.ReactNode} [props.phoneIcon] - Icon slot next to the phone.
 * @param {React.ReactNode} [props.phone] - Phone number.
 * @param {React.ReactNode} [props.scheduleIcon] - Icon slot next to the schedule.
 * @param {React.ReactNode} [props.openingTime] - Opening time.
 * @param {React.ReactNode} [props.closingTime] - Closing time.
 * @param {React.ReactNode} [props.meetingLabel] - Meeting-information caption.
 * @param {React.ReactNode} [props.meeting] - Meeting-information text.
 * @param {string} [props.className] - Extra class names for layout overrides.
 * @returns {JSX.Element}
 */
function HandlingColumn({
  titleIcon = null,
  title,
  terminalName,
  terminal = null,
  addressIcon = null,
  address = null,
  phoneIcon = null,
  phone = null,
  scheduleIcon = null,
  openingTime = null,
  closingTime = null,
  meetingLabel = null,
  meeting = null,
  className = '',
}) {
  const hasSchedule = (openingTime || closingTime)
    && !(openingTime === '00:00' && closingTime === '00:00');

  return (
    <div className={`${styles.column} ${className}`}>
      <div className={styles['title-row']}>
        {titleIcon ? <span className={styles.badge}>{titleIcon}</span> : null}
        <span className={`${styles.title} ${typo['p1-bold']}`}>{title}</span>
      </div>
      <div className={styles['terminal-row']}>
        <p className={`${styles['terminal-name']} ${typo['h5-light']} ${typo.uppercase} ${typo['spaced-letters']}`}>{terminalName}</p>
        {terminal ? <p className={`${styles.terminal} ${typo.p1}`}>{terminal}</p> : null}
      </div>
      {address ? (
        <div className={styles['info-row']}>
          {addressIcon ? <span className={styles.icon}>{addressIcon}</span> : null}
          <p className={`${styles.address} ${typo.p1}`}>{address}</p>
        </div>
      ) : null}
      {phone || hasSchedule ? (
        <div className={styles['info-row']}>
          {phone ? (
            <div className={styles['info-row-with-time']}>
              {phoneIcon ? <span className={styles.icon}>{phoneIcon}</span> : null}
              <p className={`${styles.text} ${typo.p1}`}>{phone}</p>
            </div>
          ) : null}
          {hasSchedule ? (
            <div className={styles['info-row']}>
              {scheduleIcon ? <span className={styles.icon}>{scheduleIcon}</span> : null}
              <p className={`${styles.text} ${typo.p1}`}>{openingTime ? `${openingTime} - ` : ' -'}</p>
              <p className={`${styles.text} ${typo.p1}`}>{closingTime ? `${closingTime}` : ''}</p>
            </div>
          ) : null}
        </div>
      ) : null}
      {meeting ? (
        <div className={styles.meeting}>
          <Divider variant="dashed" subhead={meetingLabel} subheadClassName={`${typo['p1-primary']} ${typo.uppercase} ${typo.primary}`} />
          <p className={`${styles.text} ${typo.p1}`}>{meeting}</p>
        </div>
      ) : null}
    </div>
  );
}

export default HandlingColumn;
